import type { ChannelAnnouncementEvent, ChannelSubShareEvent, ChannelResubEvent, ChannelViewerMilestoneEvent } from '../../lib/twitch.js';

/** Channel events with a share-worthy message, shown inline in the chat message stream. */
export type ChatVisibleEvent = ChannelAnnouncementEvent | ChannelSubShareEvent | ChannelResubEvent | ChannelViewerMilestoneEvent;

interface Props {
  event: ChatVisibleEvent;
}

const ANNOUNCEMENT_COLOR_MAP: Record<string, string> = {
  primary: 'border-violet-500 bg-violet-950/30',
  blue: 'border-blue-500 bg-blue-950/30',
  green: 'border-green-500 bg-green-950/30',
  orange: 'border-orange-500 bg-orange-950/30',
  purple: 'border-purple-500 bg-purple-950/30',
};

/**
 * Inline system message shown directly in the chat message stream (not just
 * the mod-only Aktivitäten feed) — mirrors how Twitch itself shows
 * announcements and shared sub/streak messages inline between chat messages.
 */
export default function ChatSystemLine({ event }: Props) {
  if (event.type === 'announcement') {
    const accent = ANNOUNCEMENT_COLOR_MAP[event.color] ?? ANNOUNCEMENT_COLOR_MAP.primary;
    return (
      <div className={`mx-3 my-1 px-3 py-1.5 text-sm border-l-4 rounded-sm ${accent}`}>
        <div className="flex items-center gap-1.5 text-xs text-slate-400 mb-0.5">
          <span>📢</span>
          <span className="font-semibold">{event.displayName || 'Ankündigung'}</span>
        </div>
        {event.message && <div className="text-slate-200">{event.message}</div>}
      </div>
    );
  }

  if (event.type === 'sub_share_message') {
    return (
      <div className="px-3 py-1 text-sm leading-snug break-words">
        <span className="text-purple-400 mr-1">★</span>
        <span className="font-semibold text-purple-300">{event.displayName || event.username}</span>
        <span className="text-slate-500"> abonniert: </span>
        <span className="italic text-slate-300">„{event.message}"</span>
      </div>
    );
  }

  if (event.type === 'resub') {
    return (
      <div className="px-3 py-1 text-sm leading-snug break-words">
        <span className="text-purple-400 mr-1">★</span>
        <span className="font-semibold text-purple-300">{event.displayName}</span>
        <span className="text-slate-500"> resub: </span>
        <span className="italic text-slate-300">„{event.message}"</span>
      </div>
    );
  }

  // viewer_milestone
  return (
    <div className="px-3 py-1 text-sm leading-snug break-words">
      <span className="text-amber-400 mr-1">🔥</span>
      <span className="font-semibold text-amber-300">{event.displayName || event.username}</span>
      <span className="text-slate-500"> Zuschauer-Serie: </span>
      <span className="italic text-slate-300">„{event.message}"</span>
    </div>
  );
}
